import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SERVICES from "../data/services.js";
import { ServiceIcon } from "../components/ServiceIcons";
import { IconWhatsApp } from "../components/Icons";
import { SERVICE_MESSAGES } from "../data/constants";
import useSeo from "../seo/useSeo";
import { ROUTE_META } from "../seo/seoConfig";

const OurServicesPage = () => {
  useSeo({
    title: ROUTE_META["/services"].title,
    description: ROUTE_META["/services"].description,
    canonicalPath: "/services",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const defaultTab = SERVICES.find((s) => s.id === tabParam)
    ? tabParam
    : SERVICES[0].id;

  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (tabParam) {
      const service = SERVICES.find((s) => s.id === tabParam);
      if (service) {
        setActiveTab(tabParam);
      }
    }
  }, [tabParam]);

  const handleTabClick = (id) => {
    setActiveTab(id);
    setSearchParams({ tab: id });
  };

  const buildWhatsAppMsg = (tabParam) => {
    return (
      SERVICE_MESSAGES[tabParam] ||
      "Hi, I'm interested in buying a watch. Can you help me?"
    );
  };

  const activeService = SERVICES.find((s) => s.id === activeTab) || SERVICES[0];

  const whatsappUrl = `https://wa.me/6285178228989?text=${encodeURIComponent(buildWhatsAppMsg(tabParam))}`;

  return (
    <div className="services-page">
      {/* Page Header */}
      <div className="services-header">
        <div className="services-header-inner">
          <div className="section-label">What We Offer</div>
          <h1 className="services-title">Our Services</h1>
          <div className="section-line" />
          <p className="services-subtitle">
            Whenever you need us, our in-house experts are ready to serve you.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="services-tabs-wrap">
        <div className="services-tabs">
          {SERVICES.map((s) => (
            <button
              key={s.id}
              className={`services-tab ${activeTab === s.id ? "active" : ""}`}
              onClick={() => handleTabClick(s.id)}
            >
              <span className="services-tab-icon">
                <ServiceIcon id={s.icon} size={36} />
              </span>
              <span className="services-tab-label">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Service Content */}
      <div className="services-content" key={activeTab}>
        <div className="services-content-inner">
          {/* Image */}
          <div className="services-image-wrap">
            <img
              src={activeService.image}
              alt={activeService.headline}
              className="services-image"
              style={{
                objectPosition: activeService.focalPoint || "50% 50%",
              }}
            />
            <div className="services-image-overlay" />
          </div>

          {/* Info */}
          <div className="services-info">
            <div className="detail-brand">{activeService.label}</div>
            <h2 className="services-headline">{activeService.headline}</h2>
            <div className="detail-divider" />
            <p className="services-tagline">{activeService.tagline}</p>

            <div className="services-steps">
              {activeService.steps.map((step, i) => (
                <div className="services-step" key={i}>
                  <div className="services-step-num">{i + 1}</div>
                  <div className="services-step-body">
                    <div className="services-step-title">{step.title}</div>
                    <div className="services-step-desc">{step.description}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            {activeTab === "watch-service" && (
              <div className="services-offered">
                <h3 className="services-offered-title">Services Offered</h3>
                <ul className="services-offered-list">
                  <li>Mechanism Repair</li>
                  <li>Re-Calibration</li>
                  <li>Strap Repairs</li>
                  <li>Polishing</li>
                  <li>Battery Change</li>
                  <li>Watch Cleaning</li>
                </ul>
                <p className="services-offered-note">
                  If you have an issue that isn't covered on this list, contact
                  our team and we will let you know how we can help.
                </p>
              </div>
            )}

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="services-cta"
            >
              <IconWhatsApp style={{ flexShrink: 0 }} />
              Contact Us via WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurServicesPage;
